import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import apiService from '../services/api.service';

// Interfaccia Tenant
interface Tenant {
  id: number;
  name: string;
  slug: string;
  role: string;
  isDefault?: boolean;
  logoUrl?: string;
}

// Interfaccia User estesa con info tenant
interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: string;
  tenantId?: number;
  tenantName?: string;
  tenantSlug?: string;
  tenantRole?: string;
  isPlatformAdmin?: boolean;
}

// Interfaccia AuthContext estesa
interface AuthContextType {
  user: User | null;
  tenants: Tenant[];
  currentTenant: Tenant | null;
  tenantSettings: Record<string, any>;
  isAuthenticated: boolean;
  isLoading: boolean;
  mustChangePassword: boolean;
  requiresTenantSelection: boolean;
  isPlatformAdmin: boolean;
  // Actions
  login: (email: string, password: string) => Promise<LoginResult>;
  selectTenant: (tenantId: number) => Promise<void>;
  switchTenant: (tenantId: number) => Promise<void>;
  refreshTenants: () => Promise<void>;
  logout: () => void;
  clearMustChangePassword: () => void;
}

// Risultato login
interface LoginResult {
  success: boolean;
  requiresTenantSelection: boolean;
  tenants?: Tenant[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [requiresTenantSelection, setRequiresTenantSelection] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [tenantSettings, setTenantSettings] = useState<Record<string, any>>({});
  const [selectionToken, setSelectionToken] = useState<string | null>(null);
  const [pendingUserId, setPendingUserId] = useState<number | null>(null);

  useEffect(() => {
    // Verifica se c'e un utente salvato
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    const savedMustChange = localStorage.getItem('mustChangePassword');
    const savedTenants = localStorage.getItem('tenants');
    const savedCurrentTenant = localStorage.getItem('currentTenant');
    const savedIsPlatformAdmin = localStorage.getItem('isPlatformAdmin');
    const savedTenantSettings = localStorage.getItem('tenantSettings');

    if (savedUser && token) {
      try {
        setUser(JSON.parse(savedUser));
        setMustChangePassword(savedMustChange === 'true');
        setIsPlatformAdmin(savedIsPlatformAdmin === 'true');

        if (savedTenants) {
          setTenants(JSON.parse(savedTenants));
        }
        if (savedCurrentTenant) {
          setCurrentTenant(JSON.parse(savedCurrentTenant));
        }
        if (savedTenantSettings) {
          setTenantSettings(JSON.parse(savedTenantSettings));
        }
      } catch (error) {
        console.error('Error parsing saved user:', error);
        clearStorage();
      }
    }

    setIsLoading(false);
  }, []);

  const clearStorage = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('mustChangePassword');
    localStorage.removeItem('tenants');
    localStorage.removeItem('currentTenant');
    localStorage.removeItem('isPlatformAdmin');
    localStorage.removeItem('tenantSettings');
  };

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await apiService.login(email, password);

      // Se richiede selezione tenant
      if (response.requiresTenantSelection) {
        setTenants(response.tenants || []);
        setSelectionToken(response.selectionToken);
        setPendingUserId(response.user?.id || null);
        setRequiresTenantSelection(true);
        setIsPlatformAdmin(response.isPlatformAdmin || false);
        setMustChangePassword(response.must_change_password || false);

        // Salva temporaneamente i dati per la selezione
        localStorage.setItem('tenants', JSON.stringify(response.tenants || []));
        localStorage.setItem('selectionToken', response.selectionToken || '');
        localStorage.setItem('pendingUserId', String(response.user?.id || ''));

        return {
          success: true,
          requiresTenantSelection: true,
          tenants: response.tenants
        };
      }

      // Login completato (utente con un solo tenant)
      completeLogin(response);

      return {
        success: true,
        requiresTenantSelection: false
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  }, []);

  const completeLogin = useCallback((response: any) => {
    setUser(response.user);
    setMustChangePassword(response.must_change_password || false);
    setIsPlatformAdmin(response.isPlatformAdmin || false);
    setTenants(response.tenants || []);
    setTenantSettings(response.tenantSettings || {});
    setRequiresTenantSelection(false);

    if (response.currentTenant) {
      setCurrentTenant(response.currentTenant);
      localStorage.setItem('currentTenant', JSON.stringify(response.currentTenant));
    }

    localStorage.setItem('user', JSON.stringify(response.user));
    localStorage.setItem('token', response.token);
    localStorage.setItem('mustChangePassword', String(response.must_change_password || false));
    localStorage.setItem('tenants', JSON.stringify(response.tenants || []));
    localStorage.setItem('isPlatformAdmin', String(response.isPlatformAdmin || false));
    localStorage.setItem('tenantSettings', JSON.stringify(response.tenantSettings || {}));

    // Pulisci dati temporanei
    localStorage.removeItem('selectionToken');
    localStorage.removeItem('pendingUserId');
  }, []);

  const selectTenant = useCallback(async (tenantId: number): Promise<void> => {
    try {
      // Recupera dati temporanei se non in state
      const token = selectionToken || localStorage.getItem('selectionToken');
      const userId = pendingUserId || parseInt(localStorage.getItem('pendingUserId') || '0');

      if (!token || !userId) {
        throw new Error('Sessione di selezione scaduta. Effettua nuovamente il login.');
      }

      const response = await apiService.selectTenant(userId, tenantId, token);

      // Completa il login
      completeLogin(response);

      // Pulisci state temporaneo
      setSelectionToken(null);
      setPendingUserId(null);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Errore nella selezione del tenant');
    }
  }, [selectionToken, pendingUserId, completeLogin]);

  const switchTenant = useCallback(async (tenantId: number): Promise<void> => {
    try {
      const response = await apiService.switchTenant(tenantId);

      // Aggiorna token
      localStorage.setItem('token', response.token);

      // Aggiorna tenant settings
      setTenantSettings(response.tenantSettings || {});
      localStorage.setItem('tenantSettings', JSON.stringify(response.tenantSettings || {}));

      // Aggiorna tenant corrente
      if (response.currentTenant) {
        setCurrentTenant(response.currentTenant);
        localStorage.setItem('currentTenant', JSON.stringify(response.currentTenant));

        // Aggiorna user con nuovo tenant
        if (user) {
          const updatedUser = {
            ...user,
            tenantId: response.currentTenant.id,
            tenantName: response.currentTenant.name,
            tenantSlug: response.currentTenant.slug,
            tenantRole: response.currentTenant.role
          };
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      }

      // Ricarica la pagina per aggiornare tutti i dati
      window.location.reload();
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Errore nel cambio tenant');
    }
  }, [user]);

  const logout = useCallback(() => {
    setUser(null);
    setTenants([]);
    setCurrentTenant(null);
    setTenantSettings({});
    setMustChangePassword(false);
    setRequiresTenantSelection(false);
    setIsPlatformAdmin(false);
    setSelectionToken(null);
    setPendingUserId(null);
    clearStorage();
  }, []);

  const clearMustChangePassword = useCallback(() => {
    setMustChangePassword(false);
    localStorage.setItem('mustChangePassword', 'false');
  }, []);

  const refreshTenants = useCallback(async (): Promise<void> => {
    try {
      const response = await apiService.getUserTenants();
      const updatedTenants = response.tenants || response || [];
      setTenants(updatedTenants);
      localStorage.setItem('tenants', JSON.stringify(updatedTenants));

      // Se il tenant corrente non e piu nella lista, resettalo
      if (currentTenant && !updatedTenants.find((t: Tenant) => t.id === currentTenant.id)) {
        if (updatedTenants.length > 0) {
          // Seleziona il primo tenant disponibile o quello di default
          const defaultTenant = updatedTenants.find((t: Tenant) => t.isDefault) || updatedTenants[0];
          setCurrentTenant(defaultTenant);
          localStorage.setItem('currentTenant', JSON.stringify(defaultTenant));
        } else {
          setCurrentTenant(null);
          localStorage.removeItem('currentTenant');
        }
      }
    } catch (error) {
      console.error('Error refreshing tenants:', error);
    }
  }, [currentTenant]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    tenants,
    currentTenant,
    tenantSettings,
    isAuthenticated: !!user && !requiresTenantSelection,
    isLoading,
    mustChangePassword,
    requiresTenantSelection,
    isPlatformAdmin,
    login,
    selectTenant,
    switchTenant,
    refreshTenants,
    logout,
    clearMustChangePassword,
  }), [
    user,
    tenants,
    currentTenant,
    tenantSettings,
    isLoading,
    mustChangePassword,
    requiresTenantSelection,
    isPlatformAdmin,
    login,
    selectTenant,
    switchTenant,
    refreshTenants,
    logout,
    clearMustChangePassword,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
